/**
 * Attendance API Utility Functions
 * Handles all API calls for attendance and leave requests
 */

import { apiRequest } from '../config/api';

/**
 * Check in for the day
 * @param {string} attendanceType - 'Regular' or 'Overtime'
 * @returns {Promise<Object>} Response with success status and check_in_time
 */
export const checkIn = async (attendanceType = 'Regular') => {
  const response = await apiRequest('check_in.php', {
    method: 'POST',
    body: JSON.stringify({ attendance_type: attendanceType })
  });
  return response;
};

/**
 * Check out for the day
 * @param {string} attendanceType - 'Regular' or 'Overtime'
 * @returns {Promise<Object>} Response with success status and check_out_time, total_hours
 */
export const checkOut = async (attendanceType = 'Regular') => {
  const response = await apiRequest('check_out.php', {
    method: 'POST',
    body: JSON.stringify({ attendance_type: attendanceType })
  });
  return response;
};

/**
 * Get attendance records with optional filters
 * @param {Object} params - Query parameters
 * @param {number} params.user_id - Filter by user ID (admin only)
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @param {string} params.filter - Filter type: all, week, month, date
 * @param {string} params.date - Specific date for date filter (YYYY-MM-DD)
 * @param {number} params.limit - Number of items per page
 * @param {number} params.offset - Offset for pagination
 * @param {boolean} params.view_as_admin - View as admin (for chief_doctor)
 * @returns {Promise<Object>} Response with attendance array, total, limit, offset
 */
export const getAttendance = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.user_id) queryParams.append('user_id', params.user_id);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  if (params.filter) queryParams.append('filter', params.filter);
  if (params.date) queryParams.append('date', params.date);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);
  if (params.view_as_admin) queryParams.append('view_as_admin', params.view_as_admin);
  
  const queryString = queryParams.toString();
  const url = `get_attendance.php${queryString ? '?' + queryString : ''}`;
  
  const response = await apiRequest(url);
  return response;
};

/**
 * Get attendance statistics
 * @param {Object} params - Query parameters
 * @param {number} params.user_id - Filter by user ID (admin only)
 * @param {string} params.period - Period: week, month
 * @param {boolean} params.view_as_admin - View as admin (for chief_doctor)
 * @returns {Promise<Object>} Response with stats object
 */
export const getAttendanceStats = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.user_id) queryParams.append('user_id', params.user_id);
  if (params.period) queryParams.append('period', params.period);
  if (params.view_as_admin) queryParams.append('view_as_admin', params.view_as_admin);
  
  const queryString = queryParams.toString();
  const url = `get_attendance_stats.php${queryString ? '?' + queryString : ''}`;
  
  const response = await apiRequest(url);
  return response;
};

/**
 * Submit a leave request
 * @param {Object} payload - Leave request data
 * @param {string} payload.leave_type - Leave type: Sick Leave, Casual Leave, Earned Leave, Emergency Leave, Other
 * @param {string} payload.start_date - Start date (YYYY-MM-DD)
 * @param {string} payload.end_date - End date (YYYY-MM-DD)
 * @param {string} payload.reason - Reason for leave
 * @param {string} payload.admin_notes - Admin notes (optional)
 * @returns {Promise<Object>} Response with success status and id
 */
export const submitLeaveRequest = async (payload) => {
  const response = await apiRequest('submit_leave_request.php', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  return response;
};

/**
 * Get leave requests
 * @param {Object} params - Query parameters
 * @param {number} params.user_id - Filter by user ID (admin only)
 * @param {string} params.status - Filter by status: Pending, Approved, Denied
 * @param {number} params.limit - Number of items per page
 * @param {number} params.offset - Offset for pagination
 * @param {boolean} params.view_as_admin - View as admin (for chief_doctor)
 * @returns {Promise<Object>} Response with leave_requests array, limit, offset
 */
export const getLeaveRequests = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.user_id) queryParams.append('user_id', params.user_id);
  if (params.status) queryParams.append('status', params.status);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);
  if (params.view_as_admin) queryParams.append('view_as_admin', params.view_as_admin);
  
  const queryString = queryParams.toString();
  const url = `get_leave_requests.php${queryString ? '?' + queryString : ''}`;
  
  const response = await apiRequest(url);
  return response;
};

/**
 * Approve or deny a leave request (admin only)
 * @param {number} id - Leave request ID
 * @param {string} status - Status: Approved or Denied
 * @param {string} admin_notes - Admin notes (optional)
 * @returns {Promise<Object>} Response with success status
 */
export const approveLeaveRequest = async (id, status, adminNotes = null) => {
  const response = await apiRequest('approve_leave_request.php', {
    method: 'PUT',
    body: JSON.stringify({ id, status, admin_notes: adminNotes })
  });
  return response;
};

/**
 * Update attendance record (admin only)
 * @param {number} id - Attendance ID
 * @param {Object} payload - Updated attendance data
 * @param {string} payload.check_in_time - Check in time (HH:MM:SS)
 * @param {string} payload.check_out_time - Check out time (HH:MM:SS)
 * @param {string} payload.status - Status: Present, Absent, Half Day, Leave
 * @param {string} payload.notes - Notes
 * @returns {Promise<Object>} Response with success status
 */
export const updateAttendance = async (id, payload) => {
  const response = await apiRequest('update_attendance.php', {
    method: 'PUT',
    body: JSON.stringify({ id, ...payload })
  });
  return response;
};

/**
 * Delete attendance record (admin only)
 * @param {number} id - Attendance ID
 * @returns {Promise<Object>} Response with success status
 */
export const deleteAttendance = async (id) => {
  const response = await apiRequest('delete_attendance.php', {
    method: 'DELETE',
    body: JSON.stringify({ id })
  });
  return response;
};

